
import { Component, Prop, Vue } from "vue-property-decorator";
import ServiceBlock from "@/components/V2/ServiceSection/ServiceBlock.vue";
import ServiceSection from "@/components/V2/ServiceSection/ServiceSection.vue";
import ServiceCounter from "@/components/V2/ServiceSection/ServiceCounter.vue";
import { SMAdditionalInfo, SMUser } from "@/store";
import ServiceInformation from "@/components/V2/ServiceSection/ServiceInformation.vue";
import ServicePreStage from "@/components/V2/ServiceSection/ServicePreStage.vue";
import { REmployerCompliance } from "@/pages/Layout/views/Employer/views/Compliance/REmployerCompliance";
import { REmployerTasks } from "@/pages/Layout/views/Employer/views/Tasks/REmployerTasks";
import { CompliancePackageLabel } from "@/models/company";

const PRICING_ROUTE = "Pricing";

@Component({
  components: {
    ServicePreStage,
    ServiceInformation,
    ServiceSection,
    ServiceBlock,
    ServiceCounter,
  },
})
export default class HomePageComplianceSection extends Vue {
  @Prop({ type: Object }) readonly src;

  get SUBSCRIPTIONS() {
    return {
      [CompliancePackageLabel.Trial]: {
        title: CompliancePackageLabel.Trial,
        info: "Free trial to explore compliance features",
        color: "vividorange",
      },
      [CompliancePackageLabel.Standard]: {
        title: CompliancePackageLabel.Standard,
        info: "Access to compliance features and unlimited immigration support",
        color: "deep-teal-600",
      },
      [CompliancePackageLabel.Basic]: {
        title: CompliancePackageLabel.Basic,
        info: "Access to compliance features",
        color: "deep-teal-300",
      },
      [CompliancePackageLabel.Premium]: {
        title: CompliancePackageLabel.Premium,
        info: "All-in-one compliance service to ensure compliance",
        color: "limegreen",
      },
    };
  }

  get COMPLIANCE_PRE_STAGES() {
    return {
      unavailable: {
        title: "Compliance is unavailable",
        text:
          "Once you obtain a Sponsor Licence you'll have to stay compliant with Home Office regulations",
        buttonLabel: "",
        disabled: false,
        action: null,
      },
      madeEasy: {
        title: "Compliance Made Easy",
        text:
          "After getting your Sponsor Licence, you'll need to meet strict Home Office compliance duties — but don’t worry, we’ve got you covered!",
        list: {
          title: "With Nation.better Compliance you will:",
          items: ["Stay audit-ready", "Avoid licence risks", "Save time on manual checks"],
        },
        buttonLabel: "Discover Compliance",
        disabled: false,
        action: this.goToPricing,
      },
      setup: {
        title: "Once you set up your account, you'll get access to Compliance",
        text:
          "Your free trial will begin automatically, giving you full access to tools that help you stay on top of immigration regulations",
        buttonLabel: "",
        disabled: false,
        action: null,
      },
    };
  }

  get COMPLIANCE_STATUS_STAGES() {
    return {
      onboarding: {
        title: "Compliance Onboarding",
        titleColor: "vividorangeLight",
        text: "Complete Onboarding to unlock your compliance status and all compliance features",
        buttonLabel: "Complete",
        disabled: false,
        action: this.goToOnboarding,
      },
      compliant: {
        title: "Your company is compliant",
        titleColor: "tealgreen-400",
        text: "Stay on top of your tasks to maintain compliance",
        buttonLabel: "Go To Profile",
        disabled: false,
        action: this.goToComplianceHome,
      },
      not_compliant: {
        title: "Attention needed",
        titleColor: "raspberrypink",
        text: "Your compliance might be at risk",
        buttonLabel: "Go To Profile",
        action: this.goToComplianceHome,
      },
      undetermined: {
        title: "Attention needed",
        titleColor: "raspberrypink",
        text: "Your compliance might be at risk",
        buttonLabel: "Go To Profile",
        action: this.goToComplianceHome,
      },
    };
  }

  goToComplianceHome() {
    this.$router.push(REmployerCompliance);
  }

  async goToOnboarding() {
    const userFlowId = this.src?.compliance?.onboarding_task_id;

    if (!userFlowId) return;

    try {
      await this.$router.push(`${SMUser.basePath}/tasks/${userFlowId}`);
    } catch {
      return;
    }
  }

  get trialExpiring() {
    const dtValue = this.src?.compliance?.subscription_end_date;

    if (!dtValue) return null;

    const dt = Date.parse(dtValue);

    if (!dt) return null;

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(dt);
  }

  get COMPLIANCE_INFOS() {
    return {
      avoidRisks: {
        type: "avoidRisks",
        title: "Manage your Compliance to avoid risks",
        text:
          "Activate your Compliance plan to meet Home Office requirements and avoid penalties or sponsorship restrictions",
        buttonLabel: "Activate now",
        disabled: false,
        action: this.goToPricing,
      },
      trialExpiringSoon: {
        type: "trialExpiringSoon",
        title: "Your Compliance trial is expiring soon!",
        text: `You’ll lose access to Compliance features on ${this.trialExpiring}. Ensure your company stays compliant—get a plan today.`,
        buttonLabel: "Stay compliant",
        disabled: false,
        action: this.goToPricing,
      },
      upgradeToPremium: {
        type: "upgradeToPremium",
        title: "Get Premium",
        text: "Upgrade to Premium for advanced compliance tools and support",
        buttonLabel: "Upgrade to Premium",
        buttonIcon: "CrownFilled",
        action: this.goToPricing,
      },
    };
  }

  async goToPricing() {
    try {
      await this.$router.push({
        name: PRICING_ROUTE,
      });
    } catch {
      return;
    }
  }

  title = "Compliance";

  get subscriptionTitle() {
    return this.subscription?.title || "";
  }

  get sectionProps() {
    const { title } = this;

    return {
      title,
    };
  }

  get isSponsor() {
    return SMUser.isSponsor;
  }

  get showSubscriptionBadge() {
    return this.subscription && SMUser.isSponsorConfirmation;
  }

  get complianceStatus() {
    return this.src?.compliance?.compliance_status ?? null;
  }

  get subscription() {
    if (SMUser.isComplianceBlocked) return null;

    return this.SUBSCRIPTIONS[SMUser.compliancePackageLabel];
  }

  get modifiedSubscription() {
    const element = { ...this.subscription };

    Object.assign(element, {
      title: "",
    });

    return element;
  }

  get isOnboardingCompleted() {
    return this.src?.banner?.employer_completed_account_setup_onboarding;
  }

  get preStage() {
    if (SMAdditionalInfo.hasOnboardingTask && !SMUser.isSponsorConfirmation) {
      return this.COMPLIANCE_PRE_STAGES.setup;
    }

    if (!SMUser.isSponsor) {
      return this.COMPLIANCE_PRE_STAGES.madeEasy;
    }

    return null;
  }

  get isOnboarding(): boolean {
    return (
      this.src?.compliance?.has_compliance_tasks === false &&
      this.src?.compliance?.complete_onboarding_task === false
    );
  }

  get stage() {
    if (!this.isSponsor) return null;

    if (!this.isOnboardingCompleted) return null;

    if (this.isOnboarding) {
      return this.COMPLIANCE_STATUS_STAGES.onboarding;
    }

    if (
      !SMUser.isSponsorConfirmation &&
      SMUser.isSubscriptionStatusTrial &&
      this.isOnboardingCompleted
    ) {
      return this.COMPLIANCE_STATUS_STAGES.not_compliant;
    }

    return this.COMPLIANCE_STATUS_STAGES[this.complianceStatus] ?? null;
  }

  get information() {
    if (!this.isSponsor) return null;

    if (SMUser.isSubscriptionStatusTrial && SMUser.isSponsorConfirmation) {
      return this.COMPLIANCE_INFOS.trialExpiringSoon;
    }

    if (
      !SMUser.isSponsorConfirmation &&
      SMUser.isSubscriptionStatusTrial &&
      this.isOnboardingCompleted
    ) {
      return this.COMPLIANCE_INFOS.avoidRisks;
    }

    if (SMUser.isSubscriptionStatusBlocked && this.isOnboardingCompleted) {
      return this.COMPLIANCE_INFOS.avoidRisks;
    }

    if (SMUser.isSubscriptionStatusActive && !SMUser.isPremiumSubscription) {
      return this.COMPLIANCE_INFOS.upgradeToPremium;
    }

    return null;
  }

  get complianceExpitationLabel() {
    if (!SMUser.isSponsorConfirmation) return "";

    if (SMUser.isComplianceBlocked) return "";

    if (!SMUser.complianceExpitationDate) return "";

    return `Expires on ${SMUser.complianceExpitationDate}`;
  }

  get showCounters() {
    if (SMUser.isComplianceBlocked) return false;

    if (this.isOnboarding) return false;

    return true;
  }

  get counters() {
    const { employee_tasks, employer_tasks } = this.src.compliance;

    return [
      {
        label: "Your Tasks to do",
        icon: "list-check",
        value: employer_tasks,
        buttonLabel: "View",
        action: this.goToMyTasks,
      },
      {
        label: "Employees' Tasks to do",
        icon: "hourglass",
        value: employee_tasks,
        buttonLabel: "View",
        action: this.goToEmployeesTasks,
      },
    ];
  }

  goToMyTasks() {
    this.$router.push({ path: REmployerTasks.path, query: { tab: "my_tasks" } });
  }

  goToEmployeesTasks() {
    this.$router.push({ path: REmployerTasks.path, query: { tab: "employees_tasks" } });
  }
}
